(ns client-portal.client.locations.data
  (:require [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.concepts.data :as concepts.d]
            [shared.core.components.auth0.data :as auth0.d]
            [client-portal.client.products.data :as products.d]
            [shared.core.reactive :as re]
            [shared.core.util :as util]
            [clojure.string :as s]
            [shared.core.time :as t]
            [cljs-time.core :as time]
            [cljs-time.format :as f]))

(def root-path [:locations])
(def selected-location-id-path (conj root-path :selected-location-id))
(def by-concept-id-path (conj root-path :by-concept-id))
(def by-location-id-path (conj root-path :by-location-id))

(defn by-concept-id-data-path [db concept-id]
  (conj by-concept-id-path (products.d/selected-product-by-route db) concept-id))

(defn sort-by-open-at-dt [items]
  (sort (fn [a b] (< (:opens-at-dt a) (:opens-at-dt b))) items))

(defn reset-time-string-to-midnight [cur-date]
  (-> (s/split cur-date "T")
      (first)
      (str "T00:00:00.000Z")))

(defn calc-days-between [t1-str t2-str]
  (try
    (if (and t1-str t2-str)
      (time/in-days (time/interval (f/parse (reset-time-string-to-midnight t1-str))
                                   (f/parse (reset-time-string-to-midnight t2-str))))
      0)
    (catch :default _
      0)))

(defn replace-date [cur-date replace-y-m-d]
  (->> (s/split cur-date "T")
       (last)
       (str replace-y-m-d "T")))

(defn adjust-midnight-occasions [occasions]
  (let [midnight-ts-ending "00:00:00.000Z"]
    (map (fn [{:keys [closes-at] :as occ}]
           (if (some-> closes-at (s/ends-with? midnight-ts-ending))
             (assoc occ :closes-at (s/replace closes-at midnight-ts-ending "23:59:59.000Z"))
             occ))
         occasions)))

(defn occasions-with-time-to [occasions {:keys [timezone store-all-time-to?]}]
  (let [occasions (adjust-midnight-occasions occasions)
        local-time-now (t/parse-locale-string (t/tz->locale-string timezone))
        local-time-now-y-m-d (t/as-y-m-d local-time-now)
        local-time-tomorrow-y-m-d (when local-time-now (t/as-y-m-d (time/plus local-time-now (time/days 1))))
        calc-seconds-til #(let [times [local-time-now (f/parse %)]]
                            (when (every? identity times)
                              (* (time/in-seconds (time/interval (apply min times) (apply max times)))
                                 (if (time/after? (first times) (last times)) -1 1))))]
    (map (fn [{:keys [opens-at closes-at] :as occ}]
           (if (and opens-at closes-at (or store-all-time-to?
                                           (s/starts-with? opens-at local-time-now-y-m-d)
                                           (s/starts-with? opens-at local-time-tomorrow-y-m-d)))
             (assoc occ :time-to-open (calc-seconds-til opens-at)
                        :time-to-close (calc-seconds-til closes-at))
             occ))
         occasions)))

(defn extrapolate-occasions-by-day-of-week [occasions timezone]
  (if (and timezone (not-empty occasions))
    (let [local-time-now (t/parse-locale-string (t/tz->locale-string timezone))
          local-time-now-y-m-d (t/as-y-m-d local-time-now)
          today-midnight (f/parse (str local-time-now-y-m-d "T00:00:00.000Z"))
          today-or-future-exists? (some #(some-> (:opens-at %) (f/parse) (time/after? today-midnight)) occasions)]
      (if today-or-future-exists?
        occasions
        (let [today-forward-by-day-of-week (->> (range 0 7)
                                                (map #(let [d (time/plus local-time-now (time/days %))]
                                                        [(t/as-day-of-week d) (t/as-y-m-d d)])))]
          (->> occasions
               (map #(assoc % :day-of-week (some-> (:opens-at %) (t/reformat-date-str "EEEE"))))
               (group-by :kind)
               (map (fn [[_ items]]
                      (let [by-day-of-week (group-by :day-of-week items)]
                        (map (fn [[day replace-y-m-d]]
                               (when-let [best-day (some->> (get by-day-of-week day)
                                                            (map #(assoc % :opens-at-dt (f/parse (:opens-at %))))
                                                            (sort-by-open-at-dt)
                                                            (last))]
                                 (let [days-between (calc-days-between (:opens-at best-day) (:closes-at best-day))
                                       replace-day-closes-at (replace-date (:closes-at best-day) replace-y-m-d)]
                                   (-> best-day
                                       (assoc :opens-at (replace-date (:opens-at best-day) replace-y-m-d)
                                              :closes-at (if (and (pos? days-between) replace-day-closes-at)
                                                           (-> replace-day-closes-at
                                                               (f/parse)
                                                               (time/plus (time/days days-between))
                                                               (t/as-format "yyyy-MM-dd'T'HH:mm:ss.SSSZ"))
                                                           replace-day-closes-at)
                                              :closes-days-ahead days-between)
                                       (dissoc :opens-at-dt)))))
                             today-forward-by-day-of-week))))
               (flatten)
               (filter identity)))))
    occasions))

(defn add-occasions-labels [occasions]
  (map #(assoc % :label (util/->capitalize-words (or (:label %) (:kind %))))
       occasions))

(defn occasion-open? [occ]
  (and (or (neg? (:time-to-open occ)) (= (:time-to-open occ) 0))
       (pos? (:time-to-close occ))))

(defn recalculate-time-for-location [{:keys [timezone] :as loc}]
  (cond
    (t/parse-locale-string (t/tz->locale-string timezone))
    (let [{:keys [occasions] :as loc} loc
          open? (some occasion-open? occasions)
          min-ms-to-change (some->> occasions
                                    (map (fn [{:keys [time-to-open time-to-close]}]
                                           [time-to-open time-to-close]))
                                    (flatten)
                                    (filter pos?)
                                    (apply min)
                                    (* 1000))]
      (if (nil? min-ms-to-change)
        loc
        (assoc loc :open? open?
                   :closed? (and (not open?)
                                 (->> occasions
                                      (some #(and (:time-to-open %) (:time-to-close %)))
                                      (nil?)
                                      (not)))
                   :schedule-set? true)))

    (map? loc)
    (assoc loc :schedule-set? true)

    :else
    loc))

(defn find-today-occasions [{:keys [timezone occasions]}]
  (let [today-local (f/parse (t/tz->date-string {:tz timezone :format "yyyy-MM-dd"}))
        tomorrow-local (time/plus today-local (time/days 1))
        occasions-with-dt (map (fn [{:keys [opens-at closes-at] :as occ}]
                                 (assoc occ :opens-at-dt (when opens-at (f/parse opens-at))
                                            :closes-at-dt (when closes-at (f/parse closes-at))))
                               occasions)]
    (filter (fn [{:keys [opens-at-dt closes-at-dt]}]
              (and today-local
                   opens-at-dt
                   closes-at-dt
                   (time/after? opens-at-dt today-local)
                   (time/before? opens-at-dt tomorrow-local)))
            occasions-with-dt)))

(defn format-am-pm [time-str timezone]
  (when (and time-str timezone)
    (let [time (f/parse time-str)
          local-time (time/plus time (time/seconds (t/tz-offset-seconds timezone)))]
      (f/unparse (f/formatter "h:mm A") local-time))))

(re/reg-auth0-loader
  :locations/load-locations-by-concept-id
  (fn [[concept-id] db]
    (by-concept-id-data-path db concept-id))
  (fn [db concept-id]
    (let [sel-product (products.d/selected-product-by-route db)]
      (when concept-id
        {:method :get
         :url "/api/locations"
         :query-params {:concept-id concept-id
                        :access-product sel-product}})))
  :transf (fn [resp]
            (or (:body resp) ""))
  :error-transf (fn [] []))

(re/reg-sub :locations/by-current-concept-id-
            (fn [db]
              (let [concept-id (concepts.d/current-concept-id db)
                    concept-label (some-> (concepts.d/current-concept db) :label)
                    v (when concept-id (get-in db (by-concept-id-data-path db concept-id)))]
                (cond
                  (nil? v)
                  (re/dispatch-load [:locations/load-locations-by-concept-id concept-id])

                  (util/loading? v)
                  v

                  :else
                  (mapv (fn [{:keys [label] :as l}]
                          (assoc l :label (if (and (some-> concept-label (not-empty)) (not= label concept-label))
                                            (s/trim (s/replace label concept-label ""))
                                            label)))
                        v)))))

(re/reg-sub
  :locations/location-id->label-by-current-concept-id-
  :<- [:locations/by-current-concept-id-]
  :<- [:anonymous/ctx-]
  (fn [[locations anon-ctx]]
    (some->> (util/when-not-loading locations)
             (map (fn [{:keys [id label]}]
                    [id (anon.d/location-label anon-ctx label)]))
             (into {}))))

(defn current-selected-location-id [db]
  (some-> (get-in db selected-location-id-path) (util/parse-int)))

(re/reg-sub :locations/current-selected-location-id- current-selected-location-id)

(defn current-location
  "The selected location's row from the current concept's list, as loaded — its `:label`
   is the raw location name, not the concept-stripped one `:locations/by-current-concept-id-`
   shows."
  [db]
  (let [location-id (current-selected-location-id db)
        locations (util/when-not-loading
                    (get-in db (by-concept-id-data-path db (concepts.d/current-concept-id db))))]
    (when location-id
      (first (filter #(= location-id (:id %)) locations)))))

(re/reg-event-db
  :locations/select re/middleware
  (fn [db [location-id]]
    (assoc-in db selected-location-id-path (util/parse-int location-id))))

(re/reg-auth0-loader
  :locations/load-by-location-id
  (fn [[location-id]]
    (conj by-location-id-path location-id))
  (fn [_ location-id]
    (when location-id
      {:method :post
       :url "/api/locations/by-location-id"
       :json-params {:location-id location-id}}))
  :transf (fn [resp]
            (let [items (or (some-> resp :body (util/->kebab-case-keywords)) [])]
              (if (seq items)
                (let [{:keys [id
                              brand-id
                              label
                              latitude
                              longitude
                              street-address
                              city
                              state-code
                              postal-code
                              country-code
                              telephone
                              timezone
                              metadata]} (first items)
                      occasions (map (fn [o]
                                       {:id (:occasion-id o)
                                        :opens-at (:occasion-opens-at o)
                                        :closes-at (:occasion-closes-at o)
                                        :kind (:occasion-kind o)
                                        :enabled (:occasion-enabled o)})
                                     items)]
                  (-> {:id id
                       :brand-id brand-id
                       :label label
                       :latitude latitude
                       :longitude longitude
                       :street-address street-address
                       :city city
                       :state-code state-code
                       :postal-code postal-code
                       :country-code country-code
                       :telephone telephone
                       :timezone timezone
                       :metadata metadata
                       :occasions (-> occasions
                                      (extrapolate-occasions-by-day-of-week timezone)
                                      (add-occasions-labels)
                                      (occasions-with-time-to {:timezone timezone
                                                               :store-all-time-to? true}))}
                      (recalculate-time-for-location)))
                {})))
  :error-transf (fn [] []))

(re/reg-sub :locations/by-current-location-id-
            (fn [db]
              (let [location-id (current-selected-location-id db)
                    v (when location-id (get-in db (conj by-location-id-path location-id)))]
                (if (nil? v)
                  (re/dispatch-load [:locations/load-by-location-id location-id])
                  v))))

(re/reg-event-db
  :locations/clear-selected re/middleware
  (fn [db]
    (update-in db root-path dissoc :selected-location-id)))
